import React, { useState, useEffect } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneIcon from '@mui/icons-material/Done';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import { deleteItem, api } from '../../utils/api';
import { useSession } from '../../context/SessionContext';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SubtaskDialog from './SubtaskDialog';
import SubtaskList from './SubtaskList';

const ItemList = ({ items, activeItemId, selectedItemId, onSelectItem, onStartEstimation, isFacilitator }) => {
  // console.log('[LOGGING] ItemList.js: Rendering with items prop. Count:', items ? items.length : 'N/A', items);
  const { updateSessionItems } = useSession();

  // Subtask state
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false);
  const [selectedItemForSubtasks, setSelectedItemForSubtasks] = useState(null);
  const [itemSubtasks, setItemSubtasks] = useState({}); // { itemId: [subtasks] }
  const [expandedItems, setExpandedItems] = useState({}); // { itemId: boolean }

  // Fetch subtasks for estimated items
  useEffect(() => {
    const fetchSubtasks = async () => {
      if (!items) return;

      const estimatedItems = items.filter(item =>
        item.status === 'estimated' && (item.jiraKey || item.externalId)
      );

      for (const item of estimatedItems) {
        try {
          const response = await api.get(`/items/${item.id}/subtasks`);
          if (response.data) {
            setItemSubtasks(prev => ({
              ...prev,
              [item.id]: response.data
            }));
          }
        } catch (err) {
          // Silently fail for items without subtasks
        }
      }
    };

    fetchSubtasks();
  }, [items]);

  const handleOpenSubtaskDialog = (item, e) => {
    if (e) e.stopPropagation();
    setSelectedItemForSubtasks(item);
    setSubtaskDialogOpen(true);
  };

  const handleCloseSubtaskDialog = () => {
    setSubtaskDialogOpen(false);
    setSelectedItemForSubtasks(null);
  };

  const handleSubtasksCreated = (newSubtasks) => {
    if (selectedItemForSubtasks) {
      setItemSubtasks(prev => ({
        ...prev,
        [selectedItemForSubtasks.id]: [
          ...(prev[selectedItemForSubtasks.id] || []),
          ...newSubtasks
        ]
      }));
      // Auto-expand the item to show new subtasks
      setExpandedItems(prev => ({
        ...prev,
        [selectedItemForSubtasks.id]: true
      }));
    }
  };

  const handleSubtaskUpdated = (updatedSubtask) => {
    setItemSubtasks(prev => {
      const itemId = updatedSubtask.itemId;
      return {
        ...prev,
        [itemId]: prev[itemId]?.map(s =>
          s.id === updatedSubtask.id ? updatedSubtask : s
        ) || []
      };
    });
  };

  const handleSubtaskDeleted = (subtaskId) => {
    setItemSubtasks(prev => {
      const newState = { ...prev };
      for (const itemId in newState) {
        newState[itemId] = newState[itemId].filter(s => s.id !== subtaskId);
      }
      return newState;
    });
  };

  const toggleItemExpanded = (itemId, e) => {
    if (e) e.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  if (!items || items.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(34, 197, 94, 0.1)', borderTop: '2px solid #22c55e' }}>
        <Typography variant="body2" align="center" fontWeight="bold" color="text.primary">
          No items to estimate.
        </Typography>
        <Typography variant="caption" align="center" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Add items manually or import them from JIRA.
        </Typography>
      </Paper>
    );
  }

  // Normalize items to ensure both id and _id are present
  const normalizedItems = items.map(item => ({
    ...item,
    id: item.id || item._id,
    _id: item._id || item.id,
    status: item.status || 'pending'
  }));
  
  // console.log('Normalized items:', normalizedItems);

  // Count items by status
  const pending = normalizedItems.filter(item => item.status === 'pending').length;
  const active = normalizedItems.filter(item => item.status === 'active' || item.status === 'revealed').length;
  const estimated = normalizedItems.filter(item => item.status === 'estimated').length;
  
  const handleItemClick = (itemId, e) => {
    // Prevent default behavior to avoid page jumps
    if (e) e.preventDefault();
    
    // Only select the item, don't start estimation
    // console.log('Item clicked with ID:', itemId);
    
    // Make sure we have a valid ID
    if (!itemId) {
      // console.error('Invalid item ID in handleItemClick');
      return;
    }
    
    // Call the parent component's handler
    if (typeof onSelectItem === 'function') {
      onSelectItem(itemId);
    } else {
      // console.error('onSelectItem is not a function');
    }
  };
  
  const handleStartEstimation = (itemId, e) => {
    // Prevent default behavior to avoid page jumps
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Prevent triggering the ListItemButton click
    }
    
    // Make sure we have a valid ID
    if (!itemId) {
      // console.error('Invalid item ID in handleStartEstimation');
      return;
    }
    
    if (isFacilitator) {
      // console.log('Starting estimation for item with ID:', itemId);
      
      // First select the item
      if (typeof onSelectItem === 'function') {
        onSelectItem(itemId);
      }
      
      // Add a small delay to ensure the item is selected before starting estimation
      setTimeout(() => {
        // Then start estimation
        if (typeof onStartEstimation === 'function') {
          try {
            onStartEstimation(itemId);
          } catch (error) {
            // console.error('Error starting estimation:', error);
          }
        } else {
          // console.error('onStartEstimation is not a function');
        }
      }, 100);
    }
  };
  
  const handleDeleteItem = async (itemId, e) => {
    e.stopPropagation(); // Prevent triggering the ListItemButton click
    
    try {
      await deleteItem(itemId);
      // console.log('Item deleted:', itemId);
      
      // Update the local state by filtering out the deleted item
      const updatedItems = normalizedItems.filter(item => 
        item.id !== itemId && item._id !== itemId
      );
      
      // Update the session context with the updated items
      updateSessionItems(updatedItems);
    } catch (error) {
      // console.error('Error deleting item:', error);
    }
  };

  return (
    <Paper elevation={3} sx={{ mb: 2, borderTop: '2px solid #22c55e', bgcolor: '#1a1a1a' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #2a2a2a', bgcolor: 'rgba(34, 197, 94, 0.1)' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Items to Estimate ({normalizedItems.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip 
            size="small" 
            label={`${estimated} estimated`} 
            color="success" 
            variant="outlined" 
            icon={<DoneIcon />}
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
          <Chip 
            size="small" 
            label={`${active} active`} 
            color="primary" 
            variant="outlined" 
            icon={<PlayArrowIcon />}
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
          <Chip 
            size="small" 
            label={`${pending} pending`} 
            color="default" 
            variant="outlined" 
            icon={<HourglassEmptyIcon />}
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        </Box>
      </Box>
      
      <List
        sx={{
          width: '100%',
          maxHeight: 500,
          overflow: 'auto',
          bgcolor: '#1a1a1a',
          '& .MuiListItemButton-root.active': {
            bgcolor: 'rgba(59, 130, 246, 0.15)',
          },
          p: 0,
          '& .MuiListItem-root': {
            py: 0.5,
          },
          '& .MuiListItemButton-root': {
            py: 0.75,
          }
        }}
      >
        {normalizedItems.map((item, index) => {
          // Get a consistent ID
          const itemId = item.id;
          const isActive = itemId === activeItemId || 
                          (activeItemId && item._id === activeItemId);
          const isSelected = itemId === selectedItemId || 
                          (selectedItemId && item._id === selectedItemId);
          
          let statusIcon = <HourglassEmptyIcon color="action" />;
          let statusText = 'Pending';
          let statusColor = 'default';
          
          if (item.status === 'active') {
            statusIcon = <PlayArrowIcon color="primary" />;
            statusText = 'Active';
            statusColor = 'primary';
          } else if (item.status === 'revealed') {
            statusIcon = <VisibilityIcon color="info" />;
            statusText = 'Revealed';
            statusColor = 'info';
          } else if (item.status === 'estimated') {
            statusIcon = <DoneIcon color="success" />;
            statusText = 'Estimated';
            statusColor = 'success';
          }
          
          return (
            <React.Fragment key={itemId}>
              {index > 0 && <Divider />}
              <ListItem
                disablePadding
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {item.status === 'pending' && isFacilitator && (
                      <Tooltip title="Start estimation">
                        <IconButton
                          color="primary"
                          onClick={(e) => handleStartEstimation(itemId, e)}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {item.finalEstimate && (
                      <Chip
                        label={item.finalEstimate}
                        color="primary"
                        size="small"
                        sx={{ mr: 1, fontWeight: 'bold' }}
                      />
                    )}
                    {/* Subtask controls for estimated items with Jira keys */}
                    {item.status === 'estimated' && (item.jiraKey || item.externalId) && isFacilitator && (
                      <>
                        {itemSubtasks[itemId]?.length > 0 && (
                          <Tooltip title={expandedItems[itemId] ? "Hide subtasks" : "Show subtasks"}>
                            <IconButton
                              size="small"
                              onClick={(e) => toggleItemExpanded(itemId, e)}
                              sx={{ mr: 0.5 }}
                            >
                              {expandedItems[itemId] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {itemSubtasks[itemId]?.length > 0 && (
                          <Chip
                            label={itemSubtasks[itemId].length}
                            size="small"
                            color="secondary"
                            sx={{ mr: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Tooltip title="Add subtasks">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenSubtaskDialog(item, e)}
                            color="secondary"
                            sx={{ mr: 1 }}
                          >
                            <AddTaskIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {isFacilitator && (
                      <Tooltip title="Delete item">
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(e) => handleDeleteItem(itemId, e)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
                sx={{ 
                  borderLeft: '4px solid',
                  borderLeftColor: 
                    item.status === 'active' ? 'primary.main' : 
                    item.status === 'revealed' ? 'info.main' : 
                    item.status === 'estimated' ? 'success.main' : 
                    isSelected ? 'secondary.main' :
                    'transparent'
                }}
              >
                <ListItemButton
                  selected={isSelected}
                  className={isActive ? 'active' : ''}
                  onClick={(e) => handleItemClick(itemId, e)}
                  sx={{ py: 0.5 }}
                  data-testid={`item-${itemId}`}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: isActive ? 'primary.main' :
                                isSelected ? 'secondary.main' : '#2a2a2a',
                        color: (isActive || isSelected) ? 'white' : '#a3a3a3',
                        width: 28,
                        height: 28
                      }}
                    >
                      {statusIcon}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="body2" component="span" fontWeight={(isActive || isSelected) ? 'bold' : 'normal'} sx={{ fontSize: '0.875rem' }}>
                          {item.title}
                        </Typography>
                        {item.externalId && (
                          <Chip 
                            label={item.externalId} 
                            size="small" 
                            variant="outlined"
                            color="secondary"
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        )}
                        <Chip
                          label={statusText}
                          size="small"
                          color={statusColor}
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      item.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ 
                            display: 'block', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mt: 0.25
                          }}
                        >
                          {item.description.length > 50 
                            ? `${item.description.substring(0, 50)}...` 
                            : item.description}
                        </Typography>
                      )
                    }
                  />
                </ListItemButton>
              </ListItem>
              {/* Collapsible subtask list */}
              {item.status === 'estimated' && (item.jiraKey || item.externalId) && itemSubtasks[itemId]?.length > 0 && (
                <Collapse in={expandedItems[itemId]} timeout="auto" unmountOnExit>
                  <Box sx={{ bgcolor: '#141414', py: 1 }}>
                    <SubtaskList
                      subtasks={itemSubtasks[itemId]}
                      onSubtaskUpdated={handleSubtaskUpdated}
                      onSubtaskDeleted={handleSubtaskDeleted}
                      readOnly={!isFacilitator}
                    />
                  </Box>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Subtask Dialog */}
      <SubtaskDialog
        open={subtaskDialogOpen}
        onClose={handleCloseSubtaskDialog}
        item={selectedItemForSubtasks}
        onSubtasksCreated={handleSubtasksCreated}
      />
    </Paper>
  );
};

export default ItemList; 